/**
 * FT/IN height: user types digits / optional ' ";
 * canonical form like 6'0", 5'10" via formatHeightFtInInput (e.g. on blur).
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';
/** While typing FT/IN: allow only valid chars — do not rebuild from digits each keystroke (fixes Backspace). */
export function sanitizeHeightFtInTyping(raw: string): string {
  return raw
    .replace(/[^0-9'"]/g, "")
    .replace(/('{2,})/g, "'")
    .replace(/("{2,})/g, '"')
    .slice(0, 14);
}

export function formatHeightFtInInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";

  if (digits.length === 1) {
    return digits;
  }

  let feet: number;
  let inches: number;

  if (digits.length === 2) {
    feet = parseInt(digits[0], 10);
    inches = parseInt(digits[1], 10);
    if (inches > 11) inches = 11;
    return `${feet}'${inches}"`;
  }

  if (digits.length === 3) {
    feet = parseInt(digits[0], 10);
    inches = parseInt(digits.slice(1), 10);
    if (inches > 11) inches = 11;
    return `${feet}'${inches}"`;
  }

  feet = parseInt(digits.slice(0, 2), 10);
  inches = parseInt(digits.slice(2), 10);
  if (feet > 8) feet = 8;
  if (inches > 11) inches = 11;
  return `${feet}'${inches}"`;
}

/** Parse canonical ft/in string; returns null if invalid shape */
export function parseFeetInchesHeight(value: string): { feet: number; inches: number } | null {
  const m = String(value)
    .trim()
    .match(/^(\d{1,2})\s*'\s*(\d{1,2})\s*"?\s*$/);
  if (!m) return null;
  return { feet: parseInt(m[1], 10), inches: parseInt(m[2], 10) };
}

export function validateHeightCm(value: string): string | undefined {
  const v = String(value).trim();
  if (!v) return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return "Height must be a number";
  if (n < 45) return "Minimum height should be 45 cm";
  if (n > 300) return "Maximum height allowed is 300 cm";
  return undefined;
}

export function validateHeightFtIn(value: string): string | undefined {
  const v = String(value).trim();
  if (!v) return undefined;
  let p = parseFeetInchesHeight(v);
  if (!p) {
    const formatted = formatHeightFtInInput(v);
    p = formatted ? parseFeetInchesHeight(formatted) : null;
  }
  if (!p) return 'Use format like 6\'0" (type digits, e.g. 60 → 6\'0")';
  if (p.feet < 1 || p.feet > 8) return "Feet should be between 1 and 8";
  if (p.inches < 0 || p.inches > 11) return "Inches should be between 0 and 11";
  return undefined;
}

export function validateWeightKg(value: string): string | undefined {
  const v = String(value).trim();
  if (!v) return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return "Weight must be a number";
  if (n < 1 || n > 500) return "Enter valid weight in kg (1–500)";
  return undefined;
}

export function validateWeightLbs(value: string): string | undefined {
  const v = String(value).trim();
  if (!v) return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return "Weight must be a number";
  if (n < 2 || n > 1100) return "Enter valid weight in lbs (2–1100)";
  return undefined;
}


export const getDeviceId = async () => {
    // Initialize the agent at application startup
    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;

    // Get the visitor identifier when you need it
    const result = await fp.get();
    return result.visitorId;
};